import { useState, useEffect } from 'react';

// Dictionnaire central — une clé par texte affiché, une valeur par langue.
// Toutes les pages admin vont piocher dedans via le hook useLang().
const translations = {
    fr: {
        home: 'Home',
        dashboard: 'Dashboard',
        posts: 'Posts',
        agreement: 'Agreement',
        logout: 'Log out',
        yourSpace: 'Ton espace',
        welcome: 'Bienvenue',
        dashboardSubtitle: 'Gère tes albums, tes posts, et suis leur statut de publication.',
        albums: 'Albums',
        regions: 'Régions',
        newAlbum: 'New album',
        messages: 'Messages',
        viewConversations: 'View conversations',
        emptyAlbums: "Aucun album pour l'instant — crée le premier.",
        pendingReview: 'pending review',
        rejected: 'rejected',
        yourArticles: 'Tes articles',
        newPost: 'New post',
        noPostsYet: 'No posts yet.',
        edit: 'Edit',
        view: 'View',
        delete: 'Delete',
        deletePost: 'Delete post',
        deleteConfirm: 'Entre ton mot de passe pour confirmer la suppression.',
        password: 'Mot de passe',
        cancel: 'Annuler',
        wrongPassword: 'Mot de passe incorrect.',
        newAlbumTitle: 'Nouvel album',
        uploadPhotos: 'Upload photos',
        uploadSubtitle: 'Crée un nouvel album pour ta galerie.',
        albumTitle: 'Album title',
        region: 'Region',
        location: 'Location',
        creator: 'Creator',
        selectCreator: '— Select creator —',
        albumCover: 'Album cover',
        clickSelectCover: 'Click to select cover photo (carrée recommandée)',
        description: 'Description',
        descriptionPlaceholder: "Décris l'album...",
        hero: 'Hero (bandeau)',
        clickSelectHero: 'Click to select hero image',
        photos: 'Photos',
        dragDrop: 'Drag & drop photos or click to browse',
        photosSelected: 'photo(s) sélectionnée(s)',
        publish: 'Publish',
        submitReview: 'Submit for review',
        titleRequired: 'Album title is required.',
        photoRequired: 'Please select at least one photo.',
        creatorRequired: 'Please select a creator.',
        uploading: 'Uploading photos...',
        somethingWrong: 'Something went wrong. Try again.',
        back: '← Back',
        photosCount: 'photos',
        selected: 'selected',
        editInfo: '✏️ Edit info',
        changingCover: 'Updating...',
        changeCover: '🖼️ Change cover',
        archived: '📦 Archived',
        visible: '👁 Visible',
        selectPhotos: '☑ Select photos',
        cancelSelection: '✕ Cancel selection',
        addPhotos: '+ Add photos',
        deleteCount: 'Delete',
        title: 'Title',
        creatorLabel: 'Creator',
        save: 'Save',
        saving: 'Saving...',
        descriptionLabel: 'Description',
        heroLabel: 'Hero (bandeau)',
        heroUrlPlaceholder: "URL de l'image bandeau (ou upload)",
        uploadBtn: '📎 Upload',
        profileSettings: 'Profile Settings',
        settingsSubtitle: 'Gère ton bandeau, ta photo, et tes informations publiques.',
        updateBanner: 'Update bandeau',
        clickAddBanner: 'Cliquer pour ajouter un bandeau (3:1 recommandé)',
        updatePhoto: 'Update photo',
        firstName: 'First name',
        lastName: 'Last name',
        bio: 'Bio',
        bioPlaceholder: 'Une courte description de toi ou de ton travail...',
        bioHint: 'Visible sur ta page créateur publique.',
        clickableLink: 'Lien cliquable',
        linkHint: 'Affiché comme lien cliquable sur ta page créateur.',
        orgLogo: "Logo de l'organisation",
        clickUploadLogo: 'Cliquer pour uploader le logo',
        saveChanges: 'Save changes',
        saved: 'Saved ✓',
        accountType: 'Type de compte',
        currentRole: 'Rôle actuel',
        requestRole: 'Demande de rôle / organisation',
        requestRoleModalTitle: 'Demander un rôle',
        requestedRole: 'Rôle demandé',
        roleCreator: "Creator — Upload et publication d'albums",
        roleOrg: 'Organisation — Compte institutionnel',
        roleMember: 'Member — Accès au contenu privé',
        optionalMessage: 'Message (optionnel)',
        whyRole: 'Pourquoi veux-tu ce rôle ? Décris ton travail...',
        submit: 'Soumettre',
        submitting: 'Envoi...',
        pending: '⏳ En attente',
        approved: '✓ Approuvée',
        rejectedStatus: '✕ Rejetée',
        messagesTitle: 'Messages',
        newConv: '+ Nouveau',
        noConversations: 'Pas de conversation encore.',
        selectConversation: 'Selectionner une conversation',
        writeMessage: 'Écrire un message...',
        send: 'Envoyer',
        newConversation: 'Nouvelle conversation',
        searchUser: 'Chercher un usager...',
        downloadPdf: 'Télécharger le PDF',
        loadingLabel: 'Chargement...',
        loginWelcome: 'Bon retour',
        loginSubtitle: 'Connecte-toi pour accéder à ton tableau de bord',
        usernameLabel: 'Nom d\'utilisateur',
        usernamePlaceholder: 'ex: winston',
        passwordLabel: 'Mot de passe',
        forgotPassword: 'Oublié?',
        showPw: 'Afficher',
        hidePw: 'Cacher',
        signIn: 'Se connecter',
        authenticating: 'Authentification...',
        locked: 'Verrouillé',
        noAccount: 'Pas de compte?',
        createAccount: 'Créer un compte',
        resetTitle: 'Réinitialiser le mot de passe',
        resetSubtitle: 'Entre ton nom d\'utilisateur pour recevoir un lien',
        resetLinkSent: 'Le lien de réinitialisation a été envoyé à ton courriel.',
        usernameNotFound: 'Nom d\'utilisateur introuvable.',
        sendResetLink: 'Envoyer le lien',
        linkSent: 'Lien envoyé',
        sending: 'Envoi...',
        backToSignIn: '← Retour à la connexion',
        fillAllFields: 'Remplis tous les champs.',
        invalidCreds: 'Nom d\'utilisateur ou mot de passe invalide.',
        accountDisabled: 'Ce compte a été désactivé.',
        footerContentRights: 'Contenu © ses créateurs',
        footerPlatformRights: 'Plateforme © Longden Collective',
        backHome: 'Accueil',
    },

    en: {
        home: 'Home',
        dashboard: 'Dashboard',
        posts: 'Posts',
        agreement: 'Agreement',
        logout: 'Log out',
        yourSpace: 'Your space',
        welcome: 'Welcome',
        dashboardSubtitle: 'Manage your albums, your posts, and track their publication status.',
        albums: 'Albums',
        regions: 'Regions',
        newAlbum: 'New album',
        messages: 'Messages',
        viewConversations: 'View conversations',
        emptyAlbums: 'No albums yet — create the first one.',
        pendingReview: 'pending review',
        rejected: 'rejected',
        yourArticles: 'Your articles',
        newPost: 'New post',
        noPostsYet: 'No posts yet.',
        edit: 'Edit',
        view: 'View',
        delete: 'Delete',
        deletePost: 'Delete post',
        deleteConfirm: 'Enter your password to confirm deletion.',
        password: 'Password',
        cancel: 'Cancel',
        wrongPassword: 'Wrong password.',
        newAlbumTitle: 'New album',
        uploadPhotos: 'Upload photos',
        uploadSubtitle: 'Create a new album for your gallery.',
        albumTitle: 'Album title',
        region: 'Region',
        location: 'Location',
        creator: 'Creator',
        selectCreator: '— Select creator —',
        albumCover: 'Album cover',
        clickSelectCover: 'Click to select cover photo (square recommended)',
        description: 'Description',
        descriptionPlaceholder: 'Describe the album...',
        hero: 'Hero (banner)',
        clickSelectHero: 'Click to select hero image',
        photos: 'Photos',
        dragDrop: 'Drag & drop photos or click to browse',
        photosSelected: 'photo(s) selected',
        publish: 'Publish',
        submitReview: 'Submit for review',
        titleRequired: 'Album title is required.',
        photoRequired: 'Please select at least one photo.',
        creatorRequired: 'Please select a creator.',
        uploading: 'Uploading photos...',
        somethingWrong: 'Something went wrong. Try again.',
        back: '← Back',
        photosCount: 'photos',
        selected: 'selected',
        editInfo: '✏️ Edit info',
        changingCover: 'Updating...',
        changeCover: '🖼️ Change cover',
        archived: '📦 Archived',
        visible: '👁 Visible',
        selectPhotos: '☑ Select photos',
        cancelSelection: '✕ Cancel selection',
        addPhotos: '+ Add photos',
        deleteCount: 'Delete',
        title: 'Title',
        creatorLabel: 'Creator',
        save: 'Save',
        saving: 'Saving...',
        descriptionLabel: 'Description',
        heroLabel: 'Hero (banner)',
        heroUrlPlaceholder: 'Banner image URL (or upload)',
        uploadBtn: '📎 Upload',
        profileSettings: 'Profile Settings',
        settingsSubtitle: 'Manage your banner, your photo, and your public information.',
        updateBanner: 'Update banner',
        clickAddBanner: 'Click to add a banner (3:1 recommended)',
        updatePhoto: 'Update photo',
        firstName: 'First name',
        lastName: 'Last name',
        bio: 'Bio',
        bioPlaceholder: 'A short description of you or your work...',
        bioHint: 'Visible on your public creator page.',
        clickableLink: 'Clickable link',
        linkHint: 'Shown as a clickable link on your creator page.',
        orgLogo: 'Organisation logo',
        clickUploadLogo: 'Click to upload the logo',
        saveChanges: 'Save changes',
        saved: 'Saved ✓',
        accountType: 'Account type',
        currentRole: 'Current role',
        requestRole: 'Request role / organisation',
        requestRoleModalTitle: 'Request a role',
        requestedRole: 'Requested role',
        roleCreator: 'Creator — Upload and publish albums',
        roleOrg: 'Organisation — Institutional account',
        roleMember: 'Member — Access to private content',
        optionalMessage: 'Message (optional)',
        whyRole: 'Why do you want this role? Describe your work...',
        submit: 'Submit',
        submitting: 'Sending...',
        pending: '⏳ Pending',
        approved: '✓ Approved',
        rejectedStatus: '✕ Rejected',
        messagesTitle: 'Messages',
        newConv: '+ New',
        noConversations: 'No conversations yet.',
        selectConversation: 'Select a conversation',
        writeMessage: 'Write a message...',
        send: 'Send',
        newConversation: 'New conversation',
        searchUser: 'Search user...',
        downloadPdf: 'Download PDF',
        loadingLabel: 'Loading...',
        loginWelcome: 'Welcome back',
        loginSubtitle: 'Sign in to access your dashboard',
        usernameLabel: 'Username',
        usernamePlaceholder: 'e.g. winston',
        passwordLabel: 'Password',
        forgotPassword: 'Forgot?',
        showPw: 'Show',
        hidePw: 'Hide',
        signIn: 'Sign In',
        authenticating: 'Authenticating...',
        locked: 'Locked',
        noAccount: 'Don\'t have an account?',
        createAccount: 'Create account',
        resetTitle: 'Reset Password',
        resetSubtitle: 'Enter your username to receive a reset link',
        resetLinkSent: 'Password reset link has been sent to your email.',
        usernameNotFound: 'Username not found.',
        sendResetLink: 'Send Reset Link',
        linkSent: 'Link Sent',
        sending: 'Sending...',
        backToSignIn: '← Back to sign in',
        fillAllFields: 'Please fill in all fields.',
        invalidCreds: 'Invalid username or password.',
        accountDisabled: 'This account has been disabled.',
        footerContentRights: 'Content © its creators',
        footerPlatformRights: 'Platform © Longden Collective',
        backHome: 'Home',
    },
};

// Un simple événement custom sert de "pub/sub" léger : quand une page
// change la langue, toutes les autres instances de useLang() (dans
// AdminNav, dans la page elle-même, etc.) sont notifiées et se
// remettent à jour, sans avoir besoin d'un Context/Provider au sommet
// de l'app (qu'on ne peut pas toucher facilement ici).
const LANG_KEY = 'winston_admin_lang';
const LANG_EVENT = 'winston-lang-change';

export function useLang() {
    const [lang, setLangState] = useState(() => localStorage.getItem(LANG_KEY) || 'fr');

    useEffect(() => {
        const handler = () => setLangState(localStorage.getItem(LANG_KEY) || 'fr');
        window.addEventListener(LANG_EVENT, handler);
        return () => window.removeEventListener(LANG_EVENT, handler);
    }, []);

    const setLang = (newLang) => {
        localStorage.setItem(LANG_KEY, newLang);
        setLangState(newLang);
        window.dispatchEvent(new Event(LANG_EVENT));
    };

    const t = (key) => translations[lang]?.[key] || translations.fr[key] || key;

    return { lang, setLang, t };
}